import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { FileText, Plus, Trash2, Upload, Download, StickyNote } from "lucide-react";

export default function HacizEvrakSection({ rec, canHaciz, onChanged }) {
  const rid = rec.id;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const notlar = rec.haciz_notlari || [];
  const evraklar = rec.haciz_evraklar || [];

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/records/${rid}/haciz-not`, { text: note });
      setNote(""); onChanged(data); toast.success("Haciz notu eklendi");
    } catch (err) { toast.error(err.response?.data?.detail || "Not eklenemedi"); } finally { setBusy(false); }
  };

  const delNote = async (nid) => {
    try { const { data } = await api.delete(`/records/${rid}/haciz-not/${nid}`); onChanged(data); toast.success("Silindi"); }
    catch (err) { toast.error("Silinemedi"); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/records/${rid}/haciz-evrak`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged(data); toast.success("Haciz evrakı yüklendi");
    } catch (err) { toast.error(err.response?.data?.detail || "Yüklenemedi"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (ev) => {
    try {
      const res = await api.get(`/records/${rid}/haciz-evrak/${ev.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = ev.filename || "haciz-tutanak";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error("İndirilemedi"); }
  };

  const delEvrak = async (did) => {
    try { const { data } = await api.delete(`/records/${rid}/haciz-evrak/${did}`); onChanged(data); toast.success("Belge silindi"); }
    catch (err) { toast.error("Silinemedi"); }
  };

  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm mt-6" data-testid="haciz-evrak-section">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
        <StickyNote className="w-4 h-4 text-[#B8933F]" />
        <div className="kicker">HACİZ NOTLARI & EVRAKLARI</div>
      </div>
      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notlar */}
        <div>
          <div className="kicker mb-2">HACİZ NOTLARI</div>
          {canHaciz && (
            <form onSubmit={addNote} className="flex gap-2 mb-3">
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="haciz-not-input" placeholder="Haciz notu yazın..." className="flex-1 px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
              <button type="submit" disabled={busy} data-testid="haciz-not-ekle" className="flex items-center gap-1 px-3 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-60"><Plus className="w-4 h-4" /></button>
            </form>
          )}
          {notlar.length === 0 ? (
            <div className="text-sm text-[#8C8377] py-3" data-testid="haciz-not-empty">Henüz haciz notu yok.</div>
          ) : (
            <div className="space-y-2">
              {notlar.map((n) => (
                <div key={n.id} className="flex items-start justify-between gap-2 p-2.5 border border-[#E7DECC] rounded-sm" data-testid={`haciz-not-${n.id}`}>
                  <div>
                    <div className="text-sm">{n.text}</div>
                    <div className="text-xs text-[#8C8377] mt-0.5">{n.by_name || n.by} · {fmtDate(n.at)}</div>
                  </div>
                  {canHaciz && <button onClick={() => delNote(n.id)} data-testid={`haciz-not-del-${n.id}`} className="p-1 text-[#8C8377] hover:text-[#A63A3A] shrink-0"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Evraklar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="kicker">HACİZ TUTANAĞI / EVRAKLAR</div>
            {canHaciz && (
              <label data-testid="haciz-evrak-upload" className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 border border-[#B8933F] text-[#B8933F] rounded-sm text-xs font-semibold hover:bg-[#B8933F]/5">
                <Upload className="w-3.5 h-3.5" /> {busy ? "Yükleniyor..." : "Belge Yükle"}
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" hidden disabled={busy} onChange={upload} />
              </label>
            )}
          </div>
          {evraklar.length === 0 ? (
            <div className="text-sm text-[#8C8377] py-3" data-testid="haciz-evrak-empty">Henüz evrak yüklenmedi.</div>
          ) : (
            <div className="space-y-2">
              {evraklar.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between gap-2 p-2.5 border border-[#E7DECC] rounded-sm" data-testid={`haciz-evrak-${ev.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#7C5E9B] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm truncate">{ev.filename}</div>
                      <div className="text-xs text-[#8C8377]">{ev.by_name || ev.by} · {fmtDate(ev.at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => download(ev)} data-testid={`haciz-evrak-dl-${ev.id}`} className="p-1.5 text-[#8C8377] hover:text-[#B8933F]"><Download className="w-4 h-4" /></button>
                    {canHaciz && <button onClick={() => delEvrak(ev.id)} data-testid={`haciz-evrak-del-${ev.id}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A]"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
